<template>
  <div v-if="currentArea !== null && settings !== null">

    <b-card
      no-body
      class="mb-1"
    >
      <b-card-header>
        <div class="d-flex align-items-center card-title-responsive-80">
          <feather-icon
            size="24"
            class="mr-1 d-none d-sm-block"
            icon="SettingsIcon"
          />
          <b-card-title class="ml-25">
            {{ $t('navigation.settings') }}<br>
            <small>{{ currentArea.areaName }}</small>
          </b-card-title>
        </div>
        <b-button-group class="ml-1">
          <b-button
            v-if="isUserAllowWrite"
            :disabled="!somethingChanged"
            variant="outline-primary"
            class="btn-icon"
            @click="submit()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="SaveIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('common.save') }}</span>
          </b-button>
        </b-button-group>
      </b-card-header>
    </b-card>

    <b-row
      v-if="isUserAllowWrite"
      class="match-height"
    >
      <b-col
        md="6"
        lg="6"
      >
        <settings-defaults
          :defaults="settings.defaults"
          :lookup-options="lookupOptions"
        />
      </b-col>

      <b-col
        md="6"
        lg="6"
      >
        <settings-lookups
          :refaid="currentArea.aid"
          :settings="settings.obsoletes"
        />
      </b-col>

      <b-col
        md="6"
        lg="6"
      >
        <settings-tag-colors
          :tags="settings.tags"
          :settings="settings.obsoletes"
        />
      </b-col>

      <b-col
        md="6"
        lg="6"
      >
        <settings-template
          :refaid="currentArea.aid"
          :templates="settings.templates"
          @on-submit="submit()"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col
        v-if="isUserAdmin"
        md="12"
        lg="12"
      >
        <settings-users />
      </b-col>
    </b-row>

  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardHeader, BButton, BCardTitle, BButtonGroup,
} from 'bootstrap-vue'

import authService from '@/vue.auth.service'
import lookupsService from '@/vue.lookups.service'
import { clone, toastSuccessOptions, swalCloseOptions } from '@/views/layouts/layouts.utils'
import SettingsDefaults from '@/views/settings/SettingsDefaults.vue'
import SettingsTagColors from '@/views/settings/SettingsTagColors.vue'
import SettingsTemplate from '@/views/settings/SettingsTemplate.vue'
import SettingsUsers from '@/views/settings/SettingsUsers.vue'
import SettingsLookups from '@/views/settings/SettingsLookups.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BButton,
    BCardTitle,
    BButtonGroup,
    SettingsDefaults,
    SettingsTagColors,
    SettingsTemplate,
    SettingsUsers,
    SettingsLookups,
  },
  data() {
    return {
      isUserAdmin: authService.isUserInRole('admin'),
      isUserAllowWrite: authService.isUserInRole('write'),
      currentArea: null,
      settings: null,
      lookupOptions: null,
      somethingChanged: false,
    }
  },
  async mounted() {
    await this.reload()
    // eslint-disable-next-line no-underscore-dangle
    if (!this._events.changedArea) {
      this.$root.$on('changedArea', async () => {
        await this.reload()
        this.somethingChanged = false
      })
    }
    this.$watch('settings', () => {
      this.somethingChanged = true
    }, { deep: true })
  },
  beforeDestroy() {
    this.$root.$off('changedArea')
  },
  methods: {
    async reload() {
      const area = await authService.currentArea()
      const { settings } = area

      // tags
      if (!settings.tags) settings.tags = []
      // eslint-disable-next-line no-await-in-loop
      // eslint-disable-next-line no-restricted-syntax
      for (const table of ['contributors', 'projects', 'donations']) {
        // eslint-disable-next-line no-await-in-loop
        const tagsOptions = await lookupsService.getMaps(`${table}.tags`, area.aid)
        tagsOptions.forEach(tag => {
          if (!settings.tags.find(t => t.label === tag.label) && tag.label !== ' ') {
            settings.tags.push({ label: tag.label, color: '' })
          }
        })
      }
      settings.tags.sort((a, b) => (a.label > b.label ? 1 : -1))
      // templates
      if (!settings.templates) settings.templates = []

      // obsoletes
      if (!settings.obsoletes || !settings.obsoletes.lastUpdate) settings.obsoletes = { lastUpdate: null }
      this.settings = settings
      this.somethingChanged = false

      // lookupOptions
      this.lookupOptions = {
        donationsTypeOptions: await lookupsService.getMaps('donations.type', area.aid),
        statusOptions: await lookupsService.getMaps('contributors.status', area.aid),
        stateOptions: lookupsService.getStateOptions(),
        currencyOptions: lookupsService.getCurrencyOptions(),
        paymentsTypeOptions: await lookupsService.getMaps('donations.payments', area.aid),
        categoryOptions: await lookupsService.getMaps('donations.category', area.aid),
      }
      this.currentArea = clone(area)
    },
    async submit() {
      const { aid } = this.currentArea
      await authService.saveSettings(aid, this.settings)
      this.$toast(toastSuccessOptions, { position: 'top-center' })
      this.somethingChanged = false
    },
  },
  async beforeRouteLeave(to, from, next) {
    if (this.somethingChanged) {
      const result = await this.$swal(swalCloseOptions)
      if (!result.value) {
        next(false)
        return
      }
    }
    next()
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
